'use client';

import { useMemo } from 'react';
import {
	BigNum,
	PRICE_PRECISION_EXP,
	MarketType,
} from '@velocity-exchange/sdk';
import { UIMarket } from '@velocity-exchange/common';
import NumberDisplay from 'src/components/NumberDisplay/NumberDisplay';
import useMarkPrice from 'src/hooks/useMarkPrice';
import useMarketsInfoStore from 'src/stores/useMarketsInfoStore';
import { AccountOpenPosition } from 'src/@types/positions';
import { PRESETS, withDecimals } from 'src/utils/formatNumber/presets';

interface MarkPriceCellProps {
	position: AccountOpenPosition;
}

export function MarkPriceCell({ position }: MarkPriceCellProps) {
	const { entryPrice, marketIndex, isDustPosition } = position;

	const uiMarket = UIMarket.createPerpMarket(marketIndex);
	const marketId = uiMarket.marketId;

	const markPrice = useMarkPrice(marketId);
	const getMarketInfoByIndexAndType = useMarketsInfoStore(
		(s) => s.getMarketInfoByIndexAndType
	);
	const decimalPlaces = getMarketInfoByIndexAndType(
		marketIndex,
		MarketType.PERP
	)?.genericInfo.priceDisplayDecimals;

	const priceChangePct = useMemo(() => {
		const entryNum = BigNum.from(entryPrice, PRICE_PRECISION_EXP).toNum();

		if (isDustPosition || entryNum <= 0) return '-';

		const markNum = markPrice.toNum();
		const percentNum = ((markNum - entryNum) / entryNum) * 100;

		return `(${percentNum.toFixed(2)}%)`;
	}, [entryPrice, isDustPosition, markPrice]);

	return (
		<div className="flex flex-col gap-y-xs text-left">
			<NumberDisplay
				value={markPrice}
				options={withDecimals(PRESETS.usdTruncate, decimalPlaces ?? 2)}
			/>
			<span className="text-left typo-body-xs text-text-secondary">
				{priceChangePct}
			</span>
		</div>
	);
}
